import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { WALKTHROUGH_STEP_SELECTOR_TIMEOUT_MS } from '../constants';
import type { ResolvedGuide, WalkthroughCallbacks } from '../types';
import { waitForElement } from '../wait-for-element';
import { buildWalkthroughStepContent } from '../walkthrough-block-builder';
import { BaseRenderer } from './base-renderer';
import { positionArrow } from './floating-arrow';

export interface WalkthroughRenderContext {
  guide: ResolvedGuide;
  currentStepIndex: number;
  callbacks: WalkthroughCallbacks;
}

/**
 * Renderiza UN step de un walkthrough — variante de tooltip con
 * navegación. Se destruye y re-crea por step en lugar de actualizar el
 * DOM en sitio: como cada step puede anclarse a un selector distinto,
 * recomputar todo es más simple y barato que tracking diffs.
 *
 * `render` retorna `boolean` (a diferencia de los demás renderers) para
 * señalar al controller si el ancla apareció a tiempo. Si devuelve
 * `false`, el controller cancela el walkthrough limpiamente.
 */
export class WalkthroughRenderer extends BaseRenderer<WalkthroughRenderContext, boolean> {
  async render(context: WalkthroughRenderContext): Promise<boolean> {
    const step = context.guide.guideSteps[context.currentStepIndex];
    if (!step) return false;

    const selector = step.selector ?? context.guide.activationRules.selector;
    if (typeof selector !== 'string' || selector.length === 0) return false;

    const anchor = await waitForElement(selector, WALKTHROUGH_STEP_SELECTOR_TIMEOUT_MS);
    if (!anchor) return false;

    const rawSide = (step.style as Record<string, unknown> | null | undefined)?.tooltipPlacement;
    const side: 'top' | 'bottom' | 'left' | 'right' =
      rawSide === 'top' || rawSide === 'left' || rawSide === 'right' ? rawSide : 'bottom';

    const { root } = this.createHost();
    this.applyDesign(step.style);
    const ownerDocument = root.ownerDocument ?? document;

    const tooltip = ownerDocument.createElement('div');
    tooltip.className = 'veo-walkthrough';

    const content = buildWalkthroughStepContent(
      step,
      context.currentStepIndex,
      context.guide.guideSteps.length,
      ownerDocument,
      context.callbacks,
    );
    tooltip.appendChild(content);

    // Caret que apunta al elemento anclado (se reposiciona junto al tooltip).
    const arrowEl = ownerDocument.createElement('div');
    arrowEl.className = 'veo-walkthrough-arrow';
    tooltip.appendChild(arrowEl);

    root.appendChild(tooltip);

    const updatePosition = async (): Promise<void> => {
      const { x, y, placement, middlewareData } = await computePosition(anchor, tooltip, {
        placement: side,
        middleware: [offset(10), flip(), shift({ padding: 8 }), arrow({ element: arrowEl })],
      });
      tooltip.style.left = `${x}px`;
      tooltip.style.top = `${y}px`;
      positionArrow(arrowEl, placement, middlewareData.arrow);
    };

    await updatePosition();

    const reposition = (): void => {
      void updatePosition();
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    this.registerCleanup(() => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    });

    return true;
  }
}
